import type { Parameters, Param, UtmParams } from '@/types/parameters';
import ApiService from '@/_cactus_core/ApiService';

export const useParameters = () => {
  const { setReferenceCode, setUTMParams } = useParametersStore();
  const { brand } = useRuntimeConfig().public;

  const {
    isLoading,
    referenceCode,
    utm,
    parameters: parametersFromStore,
  } = storeToRefs(useParametersStore());

  const { getCountries, getVisitorData } = useCountriesStore();

  const {
    visitorOriginalCountry,
    visitorFinalCountry,
    visitorFinalLanguage,
    visitorFinalTimezone,
    visitorData,
    allCountriesList,
  } = storeToRefs(useCountriesStore());

  const { userId, userCountry, userLanguage, userTimezone } = useUserData();

  const getParametersByCountry = (country: string): Parameters => {
    if (!Array.isArray(parametersFromStore.value)) {
      return parametersFromStore.value;
    }

    const defaultParameter =
      parametersFromStore.value.find(({ is_default }: any) => !!is_default) ||
      parametersFromStore.value[0];

    if (!!country === false) {
      return defaultParameter as Parameters;
    } else {
      const parameterByCountry = parametersFromStore.value.find(
        (parameter: any) => parameter.country === country,
      );

      return parameterByCountry || (defaultParameter as Parameters);
    }
  };

  const parameters = computed<Parameters>(() => {
    return getParametersByCountry(visitorOrUserCountry.value);
  });

  const availableCountries = computed(() => {
    if (!Array.isArray(parametersFromStore.value)) {
      return [];
    }

    return parametersFromStore.value.map((parameter: any) => parameter.country);
  });

  const defaultCountry = computed(() => {
    if (!Array.isArray(parametersFromStore.value)) {
      return '';
    }

    const defaultParam = parametersFromStore.value.find(
      (param: any) => param.is_default === 1,
    );

    if (typeof defaultParam?.country === 'string') {
      return defaultParam.country;
    }

    return brand.country;
  });

  const defaultLanguage = computed<string>(() => {
    // todo: get default language from backoffice/api

    let lang: string;

    switch (defaultCountry.value) {
      case 'BRA':
        lang = 'pt-br';
        break;

      default:
        lang = brand.language;
        break;
    }

    return lang;
  });

  const visitorFindedCountry = computed<any>(() => {
    return visitorOriginalCountry.value;
  });

  const visitorHasAvailableCountry = computed<any>(() => {
    if (visitorFinalCountry.value && visitorOriginalCountry.value) {
      return visitorFinalCountry.value === visitorOriginalCountry.value;
    }

    return false;
  });

  const visitorOrUserCountry = computed<any>(() => {
    // to test another country (Features and parameters)
    // return 'CAC';

    if (userId.value) {
      if (userCountry.value) {
        return userCountry.value;
      }
    }

    if (visitorFinalCountry.value) {
      return visitorFinalCountry.value;
    }

    return defaultCountry.value;
  });

  const visitorOrUserLanguage = computed<any>(() => {
    if (userId.value) {
      if (userLanguage.value) {
        return userLanguage.value;
      }
    }

    if (visitorFinalLanguage.value) {
      return visitorFinalLanguage.value;
    }

    return defaultLanguage.value;
  });

  const visitorOrUserTimezone = computed<any>(() => {
    if (userId.value) {
      if (userTimezone.value) {
        return userTimezone.value;
      }
    }

    return visitorFinalTimezone.value;
  });

  function getUniqueUtm(utmValue: Param) {
    if (Array.isArray(utmValue)) {
      return utmValue[0];
    }

    return utmValue;
  }

  function getUtmValue(utmValue: keyof UtmParams) {
    const storeValue = utm.value[utmValue];

    if (storeValue) {
      return getUniqueUtm(storeValue);
    }

    // localStorage pode estar indisponível em iOS Safari modo privado,
    // in-app browsers (Instagram/Telegram) ou contextos cross-origin —
    // resulta em "null is not an object" durante render do computed.
    try {
      const localStorageValue = window.localStorage?.getItem(utmValue);

      if (localStorageValue) {
        return getUniqueUtm(localStorageValue);
      }
    } catch {}

    return '';
  }

  const utmSrc = computed(() => {
    return getUtmValue('src');
  });

  const utmSource = computed(() => {
    return getUtmValue('utm_source');
  });

  const utmCampaign = computed(() => {
    return getUtmValue('utm_campaign');
  });

  const utmContent = computed(() => {
    return getUtmValue('utm_content');
  });

  const utmMedium = computed(() => {
    return getUtmValue('utm_medium');
  });

  const appSource = computed(() => {
    return getUtmValue('app_source');
  });

  async function fetchParameters() {
    isLoading.value = true;

    const endpoint = `/bookmaker-settings`;

    try {
      // const { ApiService } = useApiService();

      await ApiService.post<Parameters>(endpoint, {})
        .then((res) => {
          parametersFromStore.value = res.data as unknown as Parameters[];
        })
        .catch((error) => {
          console.error('fetchParameters error: ', error);
        })
        .finally(() => {
          isLoading.value = false;
        });
    } catch (err) {
      console.error('fetchParameters err: ', err);
      isLoading.value = false;
    }

    try {
      if (!allCountriesList.value) {
        getCountries(); // buscar os países uma vez
      }
    } catch (err) {
      console.error('Error to get countries');
    }

    try {
      if (!visitorData.value) {
        getVisitorData(); // buscar dados do visitante uma vez
      }
    } catch (err) {
      console.error('Error to get visitor data');
    }
  }

  const gaClientId = useCookie('_ga', { watch: true });

  return {
    getParametersByCountry,
    parameters,
    availableCountries,
    defaultCountry,
    defaultLanguage,
    fetchParameters,
    referenceCode,
    setReferenceCode,
    utmSrc,
    utmSource,
    utmCampaign,
    utmContent,
    utmMedium,
    appSource,
    setUTMParams,
    gaClientId,
    visitorFindedCountry,
    visitorHasAvailableCountry,
    visitorOrUserCountry,
    visitorOrUserLanguage,
    visitorOrUserTimezone,
  };
};
